/**
 * Schémas Zod — communications au client (chunk 9.AD.1).
 *
 * ## Ce que ce contrat rend possible
 *
 * Le conseiller ne copie plus un lien pour sortir écrire son courriel ailleurs. Il
 * demande depuis le dossier, et la trace reste.
 *
 * Deux intentions client, et pas une de plus dans ce chunk :
 *
 *   `client_form_invite`   — « voici le formulaire à remplir »
 *   `client_info_request`  — « il me manque cette information »
 *
 * ## Le destinataire est un contact réel, jamais une adresse libre
 *
 * Décision PO : « réutilise les contacts déjà présents plutôt que de demander une
 * adresse libre partout ». Le contrat n'accepte donc qu'un `contact_id`, et le
 * serveur vérifie qu'il appartient à l'entreprise du dossier. Une adresse saisie à
 * la main n'est pas supportée dans ce chunk — l'ajouter demanderait de dire
 * explicitement, en base et à l'écran, qu'elle n'est pas vérifiée, et ce serait une
 * décision produit à part entière.
 *
 * Un destinataire assureur ne peut pas arriver ici : il vit dans
 * `quote_request_recipients`, et le `CHECK` de la migration 0069 rend le mélange
 * impossible côté base.
 */

import { z } from "zod";

import { UuidSchema } from "./common.js";

/**
 * Les intentions d'envoi que porte `app.email_outbox`.
 *
 * `quote_request` existe depuis 9.L.1 et n'est pas modifiée. Les deux autres sont
 * ce chunk. Les rappels du chunk 3 et les relances assureur du chunk 4 en
 * ajouteront — d'où un `text` contraint côté base plutôt qu'un enum, qui s'étend
 * mais ne se réduit jamais.
 */
export const EMAIL_PURPOSES = [
  "quote_request",
  "client_form_invite",
  "client_info_request",
] as const;
export type EmailPurpose = (typeof EMAIL_PURPOSES)[number];

/** Les intentions adressées au client. */
export const CLIENT_EMAIL_PURPOSES = ["client_form_invite", "client_info_request"] as const;
export type ClientEmailPurpose = (typeof CLIENT_EMAIL_PURPOSES)[number];

/**
 * Signaux de livraison Mailgun retenus.
 *
 * `null` n'est **pas** un échec : Mailgun n'émet rien en mode log, et un webhook
 * non configuré est un état d'exploitation normal. L'interface doit donc dire
 * « aucun signal » et non « échec ».
 */
export const DELIVERY_STATUSES = [
  "delivered",
  "opened",
  "clicked",
  "bounced",
  "complained",
  "failed",
] as const;
export type DeliveryStatus = (typeof DELIVERY_STATUSES)[number];

// -----------------------------------------------------------------------------
// Demander une information au client
// -----------------------------------------------------------------------------

/**
 * `POST /v1/client-communications/info-request`
 *
 * Le message se construit **côté serveur** depuis le manquant : libellé, détails,
 * échéance. Le conseiller n'a donc rien à rédiger, et le corps du courriel ne peut
 * pas diverger de ce que le dossier affirme.
 *
 * `note` permet d'ajouter une phrase de contexte. Elle est plafonnée court et
 * échappée au rendu : c'est un complément, pas un éditeur.
 */
export const CreateClientInfoRequestSchema = z.object({
  missing_information_id: UuidSchema,
  contact_id: UuidSchema,
  note: z.string().trim().max(500).optional(),
});
export type CreateClientInfoRequestInput = z.infer<typeof CreateClientInfoRequestSchema>;

// -----------------------------------------------------------------------------
// Envoyer un formulaire au client
// -----------------------------------------------------------------------------

/**
 * `POST /v1/client-communications/form-invite`
 *
 * Deux chemins, un seul endpoint :
 *
 *   - `intake_link_id` fourni → on envoie un lien **existant** ;
 *   - `form_template_version_id` fourni → le serveur crée le lien PUIS l'envoie,
 *     en une transaction.
 *
 * Le second existe parce que le geste naturel du conseiller est « envoie ce
 * formulaire à ce client », pas « crée un lien, note le token, puis envoie-le ». Le
 * token n'est jamais renvoyé par cet endpoint — le worker le lit en base pour
 * construire l'URL, et le conseiller n'a plus besoin de le voir.
 */
export const CreateClientFormInviteSchema = z
  .object({
    project_id: UuidSchema,
    contact_id: UuidSchema,
    intake_link_id: UuidSchema.optional(),
    form_template_version_id: UuidSchema.optional(),
    /** Nombre de soumissions permises, quand le lien est créé ici. */
    max_submissions: z.number().int().min(1).max(50).optional(),
    /** Expiration, quand le lien est créé ici. */
    expires_at: z.string().datetime({ offset: true }).nullable().optional(),
    note: z.string().trim().max(500).optional(),
  })
  .refine((v) => Boolean(v.intake_link_id) !== Boolean(v.form_template_version_id), {
    message:
      "Provide exactly one of intake_link_id (send an existing link) or form_template_version_id (create then send).",
    path: ["intake_link_id"],
  });
export type CreateClientFormInviteInput = z.infer<typeof CreateClientFormInviteSchema>;

// -----------------------------------------------------------------------------
// La trace
// -----------------------------------------------------------------------------

/**
 * Une communication client, telle que le dossier la montre.
 *
 * Répond aux questions de la décision PO §6 : a-t-on déjà demandé cette
 * information, à qui, quand, par quel canal, pour quel besoin, quel formulaire, et
 * où en est la livraison.
 *
 * **Aucun token, aucune URL de lien.** `intake_link_id` suffit à savoir DE QUEL
 * lien il s'agit ; le token reste en base et ne repasse jamais par le wire après sa
 * révélation unique. Le conseiller doit pouvoir savoir que le formulaire a été
 * envoyé sans revoir le secret.
 */
export const ClientCommunicationSchema = z.object({
  id: UuidSchema,
  purpose: z.enum(CLIENT_EMAIL_PURPOSES),
  /** Toujours `email` dans ce chunk. Le champ existe pour que l'UI n'ait pas à le supposer. */
  channel: z.literal("email"),
  to_email: z.string(),
  contact_id: UuidSchema.nullable(),
  contact_name: z.string().nullable(),
  language: z.enum(["fr", "en"]),
  project_id: UuidSchema.nullable(),
  missing_information_id: UuidSchema.nullable(),
  intake_link_id: UuidSchema.nullable(),
  /** Libellé du besoin, snapshoté à la mise en file — survit à la purge du manquant. */
  subject_label: z.string().nullable(),
  status: z.enum(["queued", "sent", "failed", "expired"]),
  attempts: z.number().int().nonnegative(),
  sent_at: z.string().nullable(),
  delivery_status: z.enum(DELIVERY_STATUSES).nullable(),
  delivery_updated_at: z.string().nullable(),
  created_at: z.string(),
});
export type ClientCommunication = z.infer<typeof ClientCommunicationSchema>;

export const ClientCommunicationFilterSchema = z.object({
  project_id: UuidSchema,
});

/**
 * Variables snapshotées dans `template_vars` pour une intention client.
 *
 * Snapshot et non jointure, pour la même raison que le flux assureur copie `title`
 * et `reference` : le courriel doit se rendre même si le manquant a changé — ou
 * disparu, `missing_information` étant purgeable par la rétention.
 */
export const ClientEmailTemplateVarsSchema = z.object({
  /** Intention dérivée du schéma publié par le serveur. */
  form_action: z.enum(["sign_mandate", "complete_form"]).optional(),
  /** Nom du dossier, pour que le client sache de quoi on parle. */
  project_title: z.string(),
  /** Nom de l'entreprise cliente. */
  company_name: z.string(),
  /** Libellé de l'information demandée — absent sur une invitation de formulaire. */
  need_label: z.string().optional(),
  /** Détail utile, repris du manquant. */
  need_details: z.string().optional(),
  /** Échéance déjà formatée par le serveur : l'email n'a pas de locale. */
  due_label: z.string().optional(),
  /** Phrase de contexte ajoutée par le conseiller. */
  note: z.string().optional(),
  /** URL publique du formulaire — construite par le WORKER, jamais stockée ici. */
  form_url: z.string().optional(),
});
export type ClientEmailTemplateVars = z.infer<typeof ClientEmailTemplateVarsSchema>;
